import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, MenuItem, Paper, Select, Stack, Typography } from '@mui/material';

import { DesktopLayout, LayoutPaper } from 'src/layout';
import { ROUTES_URL } from 'src/constants/routes';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import AIManagersService, { shouldShowAIManagerInMenu } from 'src/services/AIManagersService';
import EventService from 'src/services/EventService';
import { useHistory, useLocation } from 'react-router-dom';
import ConversationDetailsPanel from 'src/screens/AIManagerDetails/ConversationDetailsPanel';
import useConversationDetailsRoute from 'src/screens/AIManagerDetails/useConversationDetailsRoute';

import { SalesFunnelBoard, ReviewQueueList, normalizeColumnsAfterMove } from './SalesFunnelBoard';

import type {
    AIManager,
    AIManagerConversation,
    AIManagerFunnelStageKey,
    AIManagerKanban,
    AIManagerStatus,
} from 'src/services/AIManagersService';

const STATUS_LABELS: Record<AIManagerStatus, string> = {
    draft: 'Черновик',
    needs_telegram: 'Telegram не подключён',
    ready_to_launch: 'Готов к запуску',
    running: 'Продаёт',
    paused: 'Пауза',
    error: 'Ошибка',
};

const CARD_BORDER = 'var(--lm-border)';
const CARD_RADIUS = '8px';

function getManagerStatusLabel(manager?: AIManager | null) {
    if (!manager) {
        return '';
    }

    return manager.statusLabel && manager.statusLabel !== manager.status
        ? manager.statusLabel
        : STATUS_LABELS[manager.status];
}

function EmptyState({
    title,
    text,
    actionLabel,
    onAction,
}: {
    title: string;
    text: string;
    actionLabel?: string;
    onAction?: () => void;
}) {
    return (
        <Paper
            variant="outlined"
            sx={{
                borderColor: CARD_BORDER,
                borderRadius: CARD_RADIUS,
                boxShadow: 'none',
                p: 3,
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
            >
                <Box>
                    <Typography variant="h2">{title}</Typography>
                    <Typography
                        mt={0.5}
                        variant="body2"
                        color="secondary.main"
                    >
                        {text}
                    </Typography>
                </Box>
                {actionLabel && onAction && (
                    <Button
                        variant="contained"
                        onClick={onAction}
                    >
                        {actionLabel}
                    </Button>
                )}
            </Stack>
        </Paper>
    );
}

export default function SalesFunnelScreen() {
    const isMobile = useIsMobileSize();
    const location = useLocation();
    const history = useHistory();
    const { search } = location;
    const details = useConversationDetailsRoute();
    const requestedManager = new URLSearchParams(search).get('managerId');
    const reviewOnly = new URLSearchParams(search).get('review') === '1';
    const requestVersion = useRef(0);
    const [reviewItems, setReviewItems] = useState<AIManagerConversation[]>([]);
    const [allManagers, setManagers] = useState<AIManager[]>(() => AIManagersService.getManagersSync());
    const managers = useMemo(
        () =>
            allManagers.filter(
                (m) => shouldShowAIManagerInMenu(m) || m.stats.waitingReview > 0 || m.id === requestedManager
            ),
        [allManagers, requestedManager]
    );
    const [fallbackManagerId, setFallbackManagerId] = useState(() => managers[0]?.id || '');
    // A targeted link must never silently open another manager's conversations.
    const selectedManagerId = requestedManager || fallbackManagerId;
    const [kanban, setKanban] = useState<AIManagerKanban | null>(null);
    const [isManagersLoading, setIsManagersLoading] = useState(!managers.length);
    const [isKanbanLoading, setIsKanbanLoading] = useState(false);
    const [error, setError] = useState('');
    const [movingConversationId, setMovingConversationId] = useState<number | null>(null);

    const selectedManager = useMemo(
        () => managers.find((manager) => manager.id === selectedManagerId) || null,
        [managers, selectedManagerId]
    );
    const hasConversations = Boolean(kanban?.total);

    const loadManagers = useCallback(async () => {
        setIsManagersLoading(true);
        setError('');

        try {
            const loadedManagers = await AIManagersService.getManagers();
            setManagers(loadedManagers);
            setFallbackManagerId((currentId) => {
                if (currentId && loadedManagers.some((manager) => manager.id === currentId)) {
                    return currentId;
                }

                return loadedManagers.find((m) => shouldShowAIManagerInMenu(m) || m.stats.waitingReview > 0)?.id || '';
            });
        } catch (_requestError) {
            setError('Не удалось загрузить AI-менеджеров.');
        } finally {
            setIsManagersLoading(false);
        }
    }, []);

    const loadKanban = useCallback(
        async (managerId: string) => {
            const version = ++requestVersion.current;
            setKanban(null);
            setReviewItems([]);
            if (!managerId) {
                setIsKanbanLoading(false);
                return;
            }

            setIsKanbanLoading(true);
            setError('');

            try {
                if (reviewOnly) {
                    const items = await AIManagersService.getReviewQueue(managerId);
                    if (version === requestVersion.current) setReviewItems(items);
                } else {
                    const board = await AIManagersService.getKanban(managerId, 80);
                    if (version === requestVersion.current) setKanban(board);
                }
            } catch (_requestError) {
                if (version === requestVersion.current)
                    setError(reviewOnly ? 'Не удалось загрузить очередь диалогов.' : 'Не удалось загрузить воронку.');
            } finally {
                if (version === requestVersion.current) setIsKanbanLoading(false);
            }
        },
        [reviewOnly]
    );

    useEffect(() => {
        void loadManagers();

        const subscriber = EventService.subscribe('AI_MANAGERS_UPDATED', () => {
            void loadManagers();
        });

        return () => {
            subscriber.unsubscribe();
        };
    }, [loadManagers]);

    useEffect(() => {
        void loadKanban(selectedManager?.id || '');
        return () => {
            requestVersion.current++;
        };
    }, [loadKanban, selectedManager?.id]);

    const handleMoveStage = useCallback(async (conversation: AIManagerConversation, stage: AIManagerFunnelStageKey) => {
        if (conversation.salesStage === stage) {
            return;
        }

        setMovingConversationId(conversation.id);

        try {
            const updated = await AIManagersService.moveConversationStage(conversation.id, stage);
            setReviewItems((items) =>
                items
                    .map((item) => (item.id === updated.id ? updated : item))
                    .filter((item) => item.status === 'human_review_required')
            );
            EventService.dispatch('AI_MANAGERS_UPDATED');
            setKanban((prev) => {
                if (!prev) {
                    return prev;
                }

                const columns = normalizeColumnsAfterMove(prev.columns, updated);
                const total = columns.reduce((sum, column) => sum + column.count, 0);

                return {
                    ...prev,
                    total,
                    columns,
                };
            });
        } catch (_requestError) {
            setError('Не удалось изменить стадию.');
        } finally {
            setMovingConversationId(null);
        }
    }, []);

    const content = (
        <Box
            display="flex"
            flexDirection="column"
            gap={2.5}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                gap={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                flexDirection={{ xs: 'column', sm: 'row' }}
            >
                <Box>
                    <Typography variant="h1">{reviewOnly ? 'Диалоги на проверку' : 'Воронка продаж'}</Typography>
                    {selectedManager && (
                        <Typography
                            mt={0.5}
                            variant="body2"
                            color="secondary.main"
                        >
                            {selectedManager.name}
                        </Typography>
                    )}
                </Box>
                {managers.length > 0 && (
                    <Select
                        size="small"
                        inputProps={{ 'aria-label': 'ИИ-менеджер' }}
                        value={selectedManager?.id || ''}
                        onChange={(event) => {
                            const params = new URLSearchParams(search);
                            params.set('managerId', event.target.value);
                            params.delete('conversation');
                            history.push({ ...location, search: `?${params.toString()}` });
                        }}
                        sx={{
                            minWidth: { xs: '100%', sm: 260 },
                            borderRadius: CARD_RADIUS,
                            bgcolor: 'var(--lm-surface)',
                        }}
                    >
                        {managers.map((manager) => (
                            <MenuItem
                                key={manager.id}
                                value={manager.id}
                            >
                                {manager.name}
                            </MenuItem>
                        ))}
                    </Select>
                )}
            </Box>

            {selectedManager && (
                <Box
                    display="flex"
                    gap={1}
                    flexWrap="wrap"
                >
                    <Chip
                        label={getManagerStatusLabel(selectedManager)}
                        sx={{ bgcolor: 'var(--lm-canvas)', color: 'var(--lm-text)', fontWeight: 700 }}
                    />
                    <Chip
                        label={
                            isKanbanLoading
                                ? 'Загрузка…'
                                : `${reviewOnly ? reviewItems.length : kanban?.total || 0} диалогов`
                        }
                        variant="outlined"
                    />
                    {!reviewOnly && selectedManager.stats.waitingReview > 0 && (
                        <Chip
                            label={`${selectedManager.stats.waitingReview} проверить`}
                            variant="outlined"
                        />
                    )}
                </Box>
            )}

            {reviewOnly && selectedManager && (
                <Button
                    sx={{ alignSelf: 'flex-start' }}
                    onClick={() => {
                        const params = new URLSearchParams(search);
                        params.set('managerId', selectedManager.id);
                        params.delete('review');
                        params.delete('conversation');
                        history.push({ ...location, search: `?${params.toString()}` });
                    }}
                >
                    Все диалоги менеджера
                </Button>
            )}
            {!isManagersLoading && requestedManager && !selectedManager && (
                <EmptyState
                    title="Менеджер недоступен"
                    text="Он мог быть удалён или ещё не настроен."
                />
            )}
            {details.conversationId && !requestedManager && (
                <EmptyState
                    title="В ссылке не указан менеджер"
                    text="Откройте диалог из карточки."
                    actionLabel="Закрыть"
                    onAction={details.close}
                />
            )}
            {error && (
                <Paper
                    variant="outlined"
                    sx={{ borderColor: CARD_BORDER, borderRadius: CARD_RADIUS, boxShadow: 'none', p: 2 }}
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                    >
                        <Typography
                            variant="body2"
                            color="var(--lm-danger-ink)"
                        >
                            {error}
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                if (selectedManagerId) {
                                    void loadKanban(selectedManagerId);
                                } else {
                                    void loadManagers();
                                }
                            }}
                        >
                            Повторить
                        </Button>
                    </Stack>
                </Paper>
            )}

            {isManagersLoading && !managers.length && (
                <Paper
                    variant="outlined"
                    sx={{ borderColor: CARD_BORDER, borderRadius: CARD_RADIUS, boxShadow: 'none', p: 3 }}
                >
                    <Typography
                        variant="body2"
                        color="secondary.main"
                    >
                        Загружаем AI-менеджеров...
                    </Typography>
                </Paper>
            )}

            {!isManagersLoading && !managers.length && !requestedManager && !error && (
                <EmptyState
                    title="AI-менеджеров пока нет"
                    text="Создайте менеджера и подключите его к потокам лидов."
                    actionLabel="Новый AI-менеджер"
                    onAction={() => history.push(ROUTES_URL.AI_MANAGER_NEW)}
                />
            )}

            {selectedManager && isKanbanLoading && (
                <Paper
                    variant="outlined"
                    sx={{ borderColor: CARD_BORDER, borderRadius: CARD_RADIUS, boxShadow: 'none', p: 3 }}
                >
                    <Typography
                        variant="body2"
                        color="secondary.main"
                    >
                        {reviewOnly ? 'Загружаем диалоги…' : 'Загружаем воронку…'}
                    </Typography>
                </Paper>
            )}

            {selectedManager && !isKanbanLoading && !(reviewOnly ? reviewItems.length : hasConversations) && !error && (
                <EmptyState
                    title={reviewOnly ? 'Нет диалогов на проверку' : 'Диалогов пока нет'}
                    text={
                        reviewOnly
                            ? 'Сейчас у этого менеджера нет обращений, ожидающих проверки.'
                            : 'Диалоги появятся после запуска менеджера.'
                    }
                    actionLabel="Открыть менеджера"
                    onAction={() =>
                        history.push(ROUTES_URL.AI_MANAGER_DETAIL.replace(':managerId', selectedManager.id))
                    }
                />
            )}

            {reviewOnly && selectedManager && !isKanbanLoading && !error && reviewItems.length > 0 && (
                <ReviewQueueList
                    conversations={reviewItems}
                    movingConversationId={movingConversationId}
                    onMove={handleMoveStage}
                    onOpen={(id) => details.open(id, selectedManager.id)}
                />
            )}
            {!reviewOnly && selectedManager && !isKanbanLoading && hasConversations && kanban && (
                <SalesFunnelBoard
                    columns={kanban.columns}
                    movingConversationId={movingConversationId}
                    onMove={handleMoveStage}
                    onOpen={(id) => details.open(id, selectedManager.id)}
                />
            )}
        </Box>
    );

    return (
        <>
            {isMobile ? (
                <LayoutPaper>{content}</LayoutPaper>
            ) : (
                <DesktopLayout hideRightSidebar>{content}</DesktopLayout>
            )}
            {requestedManager && selectedManager && details.conversationId && (
                <ConversationDetailsPanel
                    key={`${selectedManager.id}:${details.conversationId}`}
                    managerId={selectedManager.id}
                    conversationId={details.conversationId}
                    onClose={details.close}
                />
            )}
        </>
    );
}
